import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type {
  EntradaDoPreco,
  ListaDePrecos,
  PrecoPublico,
  TipoDeServicoAdministrativo,
} from './contrato';

/**
 * A camada de dados dos preços públicos dos serviços administrativos. Dado real,
 * da API (`bff/v1/admin/precos-publicos`); a regra — sem sobreposição de vigência
 * por serviço, preço que não se reescreve — é conferida no banco (migração 0320).
 *
 * Nenhum componente chama `chamarApi` direto, a chave de cache mora junto de quem
 * a usa, e toda consulta leva `retry: naoInsistirEmRecusa` (CLAUDE.md, "Regras de
 * API").
 */
export const CAMINHO_DA_API = '/bff/v1/admin/precos-publicos';

export const TAMANHO_DA_PAGINA = 20;

export const CHAVES = {
  lista: (pagina: number, tipoServico: TipoDeServicoAdministrativo | null) =>
    ['admin', 'precos-publicos', 'lista', pagina, tipoServico] as const,
  tudo: ['admin', 'precos-publicos'] as const,
};

export function usePrecosPublicos(pagina: number, tipoServico: TipoDeServicoAdministrativo | null) {
  return useQuery({
    queryKey: CHAVES.lista(pagina, tipoServico),
    queryFn: ({ signal }) => {
      const busca = new URLSearchParams({
        pagina: String(pagina),
        tamanho: String(TAMANHO_DA_PAGINA),
      });
      if (tipoServico) {
        busca.set('tipoServico', tipoServico);
      }
      return chamarApi<ListaDePrecos>(`${CAMINHO_DA_API}?${busca.toString()}`, { sinal: signal });
    },
    retry: naoInsistirEmRecusa,
  });
}

/** Toda escrita devolve o preço gravado; a lista é invalidada. */
function useEscrita<V>(executar: (v: V) => Promise<PrecoPublico>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: executar,
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVES.tudo }),
  });
}

export function useCadastrarPreco() {
  return useEscrita((entrada: EntradaDoPreco) =>
    chamarApi<PrecoPublico>(CAMINHO_DA_API, { metodo: 'POST', corpo: entrada }),
  );
}

export function useEncerrarVigencia() {
  return useEscrita((v: { id: string; vigenciaFim: string }) =>
    chamarApi<PrecoPublico>(`${CAMINHO_DA_API}/${encodeURIComponent(v.id)}/encerrar`, {
      metodo: 'POST',
      corpo: { vigenciaFim: v.vigenciaFim },
    }),
  );
}

/** Mensagem da API quando ela disse o que fazer; senão, a genérica. */
export function mensagemDoErro(erro: unknown, generica: string): string {
  return erro instanceof ErroApi && erro.message ? erro.message : generica;
}
